import { useState } from "react";

import { $enum } from "ts-enum-util";

import { PlanningDay } from "@eisox/api/core-api";
import { ActionButton, Alert, Button, Dialog, Field, Radio, Select } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { CalendarIcon, CopyIcon } from "@eisox/icons";
import * as Accordion from "@radix-ui/react-accordion";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";

import { m } from "~/paraglide/messages";

import { copySchema, TargetDays, targetDaysToDays, type CopySchemaType } from "../helpers";

interface Place {
  id: string;
  name: string;
  parentName?: string;
  /** Domain ids the place stands for (e.g. every unit of an AHU group). Defaults to `[id]`. */
  parentIds?: string[];
}

interface CopyPlanningDialogProps extends Dialog.RootProps {
  source: Place;
  resource: "room" | "network" | "airHandlerUnit";
  places: Place[];
  onCopy?: (data: CopySchemaType) => void;
  disabled?: boolean;
}

type CopyFormValues = {
  mode: "by-day" | "to-another-place";
  dayToCopy: PlanningDay;
  targetDays: PlanningDay[];
  daysToCopy: PlanningDay[];
  targetPlaces: string[];
};

const defaultValues: CopyFormValues = {
  mode: "by-day",
  dayToCopy: PlanningDay.MONDAY,
  targetDays: targetDaysToDays[TargetDays.MondayToFriday],
  daysToCopy: targetDaysToDays[TargetDays.MondayToSunday],
  targetPlaces: [],
};

export const CopyPlanningDialog: React.FC<CopyPlanningDialogProps> = ({
  source,
  resource,
  places,
  onCopy,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  disabled,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });
  const [targetDays, setTargetDays] = useState<TargetDays>(TargetDays.MondayToFriday);
  const [daysToCopy, setDaysToCopy] = useState<TargetDays>(TargetDays.MondayToSunday);

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = copySchema.safeParse(value);
        if (!result.success) return result.error;
      },
    },
    onSubmit: ({ value }) => {
      onCopy?.(copySchema.parse(value));
      handleOpenChange(false);
    },
  });

  const dayToCopy = useSelector(form.store, state => state.values.dayToCopy);

  const daysOptions = $enum(PlanningDay)
    .getValues()
    .map(day => ({ name: m[day](), value: day }));

  const targetDaysOptions = [
    { name: `${m.monday()} - ${m.friday()}`, value: TargetDays.MondayToFriday },
    { name: `${m.saturday()} - ${m.sunday()}`, value: TargetDays.SaturdayToSunday },
    { name: `${m.monday()} - ${m.sunday()}`, value: TargetDays.MondayToSunday },
    { name: m.personalized(), value: TargetDays.Custom },
  ];

  const placesOptions = places
    .filter(p => p.id !== source.id)
    .reduce(
      (acc, { id, name, parentName }) => {
        const key = parentName ?? "undefined";
        (acc[key] ??= []).push({ value: id, name });
        return acc;
      },
      {} as Record<string, { value: string; name: string }[]>,
    );
  Object.values(placesOptions).forEach(group => group.sort((a, b) => a.name.localeCompare(b.name)));

  const handleOpenChange = (nextOpen: boolean) => {
    form.reset(defaultValues);
    setTargetDays(TargetDays.MondayToFriday);
    setDaysToCopy(TargetDays.MondayToSunday);
    setOpen(nextOpen);
  };

  const handleChangeTargetDays = (value: string | string[]) => {
    setTargetDays(value as TargetDays);
    form.setFieldValue("targetDays", targetDaysToDays[value as TargetDays]);
  };

  const handleChangeDaysToCopy = (value: string | string[]) => {
    setDaysToCopy(value as TargetDays);
    form.setFieldValue("daysToCopy", targetDaysToDays[value as TargetDays]);
  };

  return (
    <Dialog.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button disabled={disabled}>
          <CopyIcon />
        </Button>
      </Dialog.Trigger>
      <Dialog.Content title={m.duplicate_user_plannings()} icon={<CalendarIcon />}>
        <form
          className="w-132.5 gap-10 flex flex-col items-center text-base"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.Field name="mode">
              {field => (
                <Accordion.Root
                  className="w-full"
                  type="single"
                  defaultValue="by-day"
                  value={field.state.value}
                  onValueChange={v => field.handleChange(v as CopyFormValues["mode"])}
                >
                  <Accordion.Item value="by-day">
                    <Accordion.Header className="flex [all:unset]">
                      <Accordion.Trigger className="gap-1.25 font-medium flex cursor-pointer items-center">
                        <Radio checked={field.state.value === "by-day"} />
                        {`${source.name} : ${m.duplicate_by_day()}`}
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div className="m-2.5 gap-2.5 flex flex-col">
                        <form.AppField name="dayToCopy">
                          {field => (
                            <field.Select
                              label={m.day_to_duplicate()}
                              classNames={{ control: "w-full" }}
                              multiple={false}
                              options={daysOptions}
                              searchInput={false}
                            />
                          )}
                        </form.AppField>
                        <Field.Root className="w-full">
                          <Field.Label>{m.to()}</Field.Label>
                          <div className="gap-2.5 *:min-w-0 flex w-full *:flex-1">
                            <Select.Root multiple={false} value={targetDays} onValueChange={handleChangeTargetDays}>
                              <Select.Trigger className="w-full">
                                <Select.Value />
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Viewport>
                                  {targetDaysOptions.map(option => (
                                    <Select.Item key={option.value} value={option.value}>
                                      {option.name}
                                    </Select.Item>
                                  ))}
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Root>
                            {targetDays === TargetDays.Custom && (
                              <form.AppField name="targetDays">
                                {field => (
                                  <field.Select
                                    classNames={{ control: "w-full" }}
                                    multiple
                                    placeholder={m.select_days()}
                                    searchInput={false}
                                    options={daysOptions.filter(day => day.value !== dayToCopy)}
                                  />
                                )}
                              </form.AppField>
                            )}
                          </div>
                        </Field.Root>
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                  <Accordion.Item value="to-another-place">
                    <Accordion.Header className="flex [all:unset]">
                      <Accordion.Trigger className="gap-1.25 font-medium flex cursor-pointer items-center">
                        <Radio checked={field.state.value === "to-another-place"} />
                        {m.duplicate_to_another_place({ resource, source: source.name })}
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div className="m-2.5 gap-2.5 flex flex-col">
                        <Field.Root>
                          <Field.Label>{m.days_to_duplicate({ resource, source: source.name })}</Field.Label>
                          <div className="gap-2.5 *:min-w-0 flex w-full *:flex-1">
                            <Select.Root multiple={false} value={daysToCopy} onValueChange={handleChangeDaysToCopy}>
                              <Select.Trigger className="w-full">
                                <Select.Value />
                              </Select.Trigger>
                              <Select.Content>
                                <Select.Viewport>
                                  {targetDaysOptions.map(option => (
                                    <Select.Item key={option.value} value={option.value}>
                                      {option.name}
                                    </Select.Item>
                                  ))}
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Root>
                            {daysToCopy === TargetDays.Custom && (
                              <form.AppField name="daysToCopy">
                                {field => (
                                  <field.Select
                                    classNames={{ control: "w-full" }}
                                    multiple
                                    placeholder={m.select_days()}
                                    searchInput={false}
                                    options={daysOptions}
                                  />
                                )}
                              </form.AppField>
                            )}
                          </div>
                        </Field.Root>
                        <form.AppField name="targetPlaces">
                          {field => (
                            <Field.Root>
                              <Field.Label>{m.duplicate_to({ resource, source: source.name })}</Field.Label>
                              <Select.Root multiple value={field.state.value} onValueChange={field.handleChange}>
                                <Select.Trigger className="w-full" onBlur={field.handleBlur}>
                                  <Select.Value placeholder={m.select_places({ resource })} />
                                </Select.Trigger>
                                <Select.Content>
                                  {Object.values(placesOptions).flat().length > 10 && (
                                    <Select.Input placeholder={m.search()} />
                                  )}
                                  <Select.Viewport>
                                    {Object.entries(placesOptions).flatMap(([parentName, groupPlaces]) =>
                                      groupPlaces.map(({ name, value }) => (
                                        <Select.Item key={value} value={value}>
                                          {name}
                                          {parentName !== "undefined" && (
                                            <span className="text-sm text-primary-dark-gray">{parentName}</span>
                                          )}
                                          <Select.ItemText>{name}</Select.ItemText>
                                        </Select.Item>
                                      )),
                                    )}
                                    <Select.Empty>{m.no_result()}</Select.Empty>
                                  </Select.Viewport>
                                </Select.Content>
                              </Select.Root>
                            </Field.Root>
                          )}
                        </form.AppField>
                        <Alert>{m.duplicate_alert({ resource })}</Alert>
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                </Accordion.Root>
              )}
            </form.Field>
            <div className="gap-5 flex items-center">
              <Dialog.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Dialog.Close>
              <form.SubmitButton label={m.duplicate()} />
            </div>
          </form.AppForm>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export type { CopyPlanningDialogProps, Place };
